import { samePolicyRoot } from '@ourbranch/policy-utils';

/**
 * #33009: decide whether the farm endorsement UW questionnaire/attestation/note may be skipped.
 * Both must hold:
 *  - the editing user has the UW role (canApproveUWReview), AND
 *  - the policy term was CREATED BY RENEWING an imported/migrated term
 *    (`policy.wasRenewedFromImportedTerm`, set by the renewal creator on every renewal in a
 *    migrated lineage).
 *
 * There is no time window: the skip stays available for the life of a migrated policy, matching
 * "all migrated-and-then-renewed policies". Ordinary new farm business (no import in its lineage)
 * and the original locked/read-only imported term (created by the converter, not a renewal) never
 * carry the flag, so they always keep the full requirements — and non-UW users always do too.
 */
export const shouldRelaxFarmUWRequirements = ({ canApproveUWReview, wasRenewedFromImportedTerm } = {}) =>
  Boolean(canApproveUWReview) && Boolean(wasRenewedFromImportedTerm);

/**
 * True when the policy's LINEAGE was renewed from an imported/migrated term.
 *
 * `wasRenewedFromImportedTerm` is set by the renewal creator on RENEWALS only, so the original
 * imported term (the converter-created base) never carries it. But the FE surfaces that in-force
 * imported term as the current `policy`, while a UW actually endorses the (editable) future renewal —
 * which does carry the flag. Reading `policy.wasRenewedFromImportedTerm` alone therefore misses every
 * migrated policy whose renewal is being serviced. Check the whole account lineage instead (matching
 * on account + LOB via samePolicyRoot, so it never crosses into a different policy), so the migration
 * relaxations (roof shape, RC/RCE Coverage E validations) fire for the renewal. #33509
 */
export const wasRenewedFromImportedLineage = (policy, policies = []) => {
  if (policy?.wasRenewedFromImportedTerm) {
    return true;
  }
  if (!policy?.id) {
    return false;
  }
  return (policies || []).some(
    (term) => term?.id && term.wasRenewedFromImportedTerm && samePolicyRoot(term.id, policy.id)
  );
};

/**
 * Resolve the current farm policy restriction from its segments.
 *
 * Walks segments newest → oldest and returns the first segment where
 * `global.farmVerification.restrictions` is *defined* — including an empty string, which means a
 * UW deliberately cleared it on a newer segment (treating "" as "absent" would resurface an older
 * segment's restriction after a UW removed it, #32074). Returns the trimmed value, or null when
 * absent/blank. Shared by the policy-restrictions banner and the Details restriction section so
 * both reflect what a UW last set. (#32405, #32036)
 */
export const getFarmPolicyRestriction = (policy) => {
  const segments = policy?.segments || [];
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const restrictions = segments[i]?.global?.farmVerification?.restrictions;
    if (restrictions !== undefined && restrictions !== null) {
      return restrictions.trim() || null;
    }
  }
  return null;
};

export const getFarmUWInitialValues = (farmVerification = {}) => {
  const initialValues = {
    // Eligibility questions
    unrepairedDamage: farmVerification?.unrepairedDamage?.toString() || null,
    unrepairedDamageDetails: farmVerification?.unrepairedDamageDetails || '',
    priorFireLoss: farmVerification?.priorFireLoss?.toString() || null,
    priorFireLossDetails: farmVerification?.priorFireLossDetails || '',
    priorLiabilityLoss: farmVerification?.priorLiabilityLoss?.toString() || null,
    priorLiabilityLossDetails: farmVerification?.priorLiabilityLossDetails || '',
    chargedWithFelony: farmVerification?.chargedWithFelony?.toString() || null,
    chargedWithFelonyDetails: farmVerification?.chargedWithFelonyDetails || '',
    foreclosureBankruptcy: farmVerification?.foreclosureBankruptcy?.toString() || null,
    foreclosureBankruptcyDetails: farmVerification?.foreclosureBankruptcyDetails || '',
    coverageDeclined: farmVerification?.coverageDeclined?.toString() || null,
    coverageDeclinedDetails: farmVerification?.coverageDeclinedDetails || '',

    // Property risk factor questions
    otherResidences: farmVerification?.otherResidences?.toString() || null,
    otherResidencesDetails: farmVerification?.otherResidencesDetails || '',
    dwellingsForSale: farmVerification?.dwellingsForSale?.toString() || null,
    dwellingsForSaleDetails: farmVerification?.dwellingsForSaleDetails || '',
    unlistedStructures: farmVerification?.unlistedStructures?.toString() || null,
    unlistedStructuresDetails: farmVerification?.unlistedStructuresDetails || '',
    recreationalVehicles: farmVerification?.recreationalVehicles?.toString() || null,
    recreationalVehiclesDetails: farmVerification?.recreationalVehiclesDetails || '',
    livestockFencing: farmVerification?.livestockFencing?.toString() || null,
    livestockFencingDetails: farmVerification?.livestockFencingDetails || '',
    livestockEscape: farmVerification?.livestockEscape?.toString() || null,
    livestockEscapeDetails: farmVerification?.livestockEscapeDetails || '',
    solarPanels: farmVerification?.solarPanels?.toString() || null,
    solarPanelsDetails: farmVerification?.solarPanelsDetails || '',
    dogsOrExoticPets: farmVerification?.dogsOrExoticPets?.toString() || null,
    dogsOrExoticPetsDetails: farmVerification?.dogsOrExoticPetsDetails || '',
    trampoline: farmVerification?.trampoline?.toString() || null,
    trampolineDetails: farmVerification?.trampolineDetails || '',
    dwellingsUnderRenovation: farmVerification?.dwellingsUnderRenovation?.toString() || null,
    dwellingsUnderRenovationDetails: farmVerification?.dwellingsUnderRenovationDetails || '',
    farmStructuresRentedToOthers: farmVerification?.farmStructuresRentedToOthers?.toString() || null,
    farmStructuresRentedToOthersDetails: farmVerification?.farmStructuresRentedToOthersDetails || '',

    // Operational details questions
    businessConductedOnPremises: farmVerification?.businessConductedOnPremises?.toString() || null,
    businessConductedOnPremisesDetails: farmVerification?.businessConductedOnPremisesDetails || '',
    dayCareBusiness: farmVerification?.dayCareBusiness?.toString() || null,
    dayCareBusinessDetails: farmVerification?.dayCareBusinessDetails || '',
    anhydrousAmmoniaEquipment: farmVerification?.anhydrousAmmoniaEquipment?.toString() || null,
    anhydrousAmmoniaEquipmentDetails: farmVerification?.anhydrousAmmoniaEquipmentDetails || '',
    processOrManufacture: farmVerification?.processOrManufacture?.toString() || null,
    processOrManufactureDetails: farmVerification?.processOrManufactureDetails || '',
    huntingOrFishingFee: farmVerification?.huntingOrFishingFee?.toString() || null,
    huntingOrFishingFeeDetails: farmVerification?.huntingOrFishingFeeDetails || '',
    customFarming: farmVerification?.customFarming?.toString() || null,
    customFarmingDetails: farmVerification?.customFarmingDetails || '',

    // CFM-only risk-level question (issue #32176). Present in initial values for every brand so
    // Formik initializes the field on submit.
    namedInsuredIsPropertyOwner: farmVerification?.namedInsuredIsPropertyOwner?.toString() || null,
    namedInsuredIsPropertyOwnerDetails: farmVerification?.namedInsuredIsPropertyOwnerDetails || '',

    // Policy restrictions
    restrictions: farmVerification?.restrictions || '',

    // Attestation
    signedFarmUWAttestation: farmVerification?.signedFarmUWAttestation || false
  };

  return initialValues;
};

export default getFarmUWInitialValues;
